<template>
  <div class="report-page">

    <!-- ================================= -->
    <!-- FILTERS - EXISTING LAYOUT        -->
    <!-- ================================= -->

    <div class="dropdown-container">

      <!-- FSM -->
      <div class="dropdown-item">
        <label for="fsmSelect">FSM</label>

        <select id="fsmSelect" v-model="selectedFsm" :disabled="isFsmLocked" @change="onFsmChange">
          <option value="">Select FSM</option>

          <option v-if="isOtherUser" :value="ALL">
            All FSMs
          </option>

          <option v-for="fsm in fsmList" :key="fsm.profile_id ?? fsm.id" :value="String(fsm.profile_id)">
            {{ fsm.name }}
          </option>
        </select>
      </div>

      <!-- ASM -->
      <div class="dropdown-item">
        <label for="asmSelect">ASM</label>

        <select id="asmSelect" v-model="selectedAsm" :disabled="isAsmLocked" @change="onAsmChange">
          <option value="">Select ASM</option>

          <option v-if="isFsmUser || isOtherUser" :value="ALL">
            All ASMs
          </option>

          <option v-for="asm in asmList" :key="asm.profile_id ?? asm.id" :value="String(asm.profile_id)">
            {{ asm.name }}
          </option>
        </select>
      </div>

      <!-- DISTRIBUTOR -->
      <div class="dropdown-item">
        <label for="distributorSelect">
          Distributor
        </label>

        <select id="distributorSelect" v-model="selectedDistributor" :disabled="isDistributorLocked"
          @change="onDistributorChange">
          <option value="">
            Select Distributor
          </option>

          <option v-if="isFsmUser || isAsmUser || isOtherUser" :value="ALL">
            All Distributors
          </option>

          <option v-for="distributor in distributorList" :key="distributor.profile_id ?? distributor.id"
            :value="String(distributor.profile_id)">
            {{ distributor.name }}
          </option>
        </select>
      </div>

      <!-- DEALER -->
      <div class="dropdown-item">
        <label for="dealerSelect">Dealer</label>

        <select id="dealerSelect" v-model="selectedDealer" :disabled="!canUseDealerFilter">
          <option value="">All Dealers</option>

          <option v-for="dealer in dealerList" :key="dealer.profile_id ?? dealer.id" :value="String(dealer.profile_id)"
            :class="{
              'inactive-dealer-option': isInactiveDealer(dealer)
            }" :style="isInactiveDealer(dealer)
              ? {
                backgroundColor: '#ffebee',
                color: '#000000'
              }
              : {}
              ">
            {{ dealer.name }}{{ isInactiveDealer(dealer) ? ' (Inactive)' : '' }}
          </option>
        </select>
      </div>

      <!-- START DATE -->
      <div class="dropdown-item">
        <label for="startDatePicker">
          Start Date
        </label>

        <input id="startDatePicker" v-model="startDate" type="date" class="search-input" :max="currentDate"
          @change="handleDateChange" @blur="validateStartDate(true)" />
      </div>

      <!-- END DATE -->
      <div class="dropdown-item">
        <label for="endDatePicker">
          End Date
        </label>

        <input id="endDatePicker" v-model="endDate" type="date" class="search-input" :max="currentDate"
          @change="handleDateChange" @blur="validateEndDate(true)" />
      </div>

      <!-- SEARCH AND EXPORT -->
      <div class="search-container">

        <button type="button" class="btn button-default" :disabled="isLoading" @click="performSearch">
          <i v-if="isLoading" class="fa fa-spinner fa-spin me-1"></i>

          {{ isLoading ? "Searching..." : "Search" }}
        </button>

        <button type="button" class="btn button-export" :disabled="!preparedVisits.length || isLoading"
          @click="exportToExcel">
          Export to Excel
        </button>

      </div>

    </div>

    <!-- ================================= -->
    <!-- LOADING STATE                    -->
    <!-- ================================= -->

    <div v-if="isLoading" class="report-message" role="status">
      <i class="fa fa-spinner fa-spin"></i>
      Loading dealer visits...
    </div>

    <!-- ================================= -->
    <!-- DEALER VISIT REPORT              -->
    <!-- ================================= -->

    <section v-else-if="searchedOnce && preparedVisits.length" class="report-card">

      <!-- REPORT HEADER -->
      <div class="report-toolbar">

        <div class="report-toolbar-heading">
          <h3>Dealer Visit Report</h3>
        </div>

        <span class="report-range">
          {{ startDate }} to {{ endDate }}
        </span>

      </div>

      <!-- ================================= -->
      <!-- SIMPLIFIED FIVE-COLUMN TABLE     -->
      <!-- ================================= -->

      <div class="report-table-wrap">

        <table class="report-table dealer-summary-table">

          <thead>
            <tr>
              <th class="dealer-col">
                Dealer Name
              </th>

              <th class="remark-col">
                Remark
              </th>

              <th class="date-col">
                Date | Time
              </th>

              <th class="visited-col">
                Visited By
              </th>

              <th class="details-col">
                Details
              </th>
            </tr>
          </thead>

          <tbody>

            <tr v-for="visit in pageVisits" :key="visit.id" :class="[
              'visit-row',
              {
                'inactive-dealer-row': isInactiveDealer(visit)
              }
            ]">

              <!-- DEALER -->
              <td class="dealer-cell">

                <span class="dealer-name" :title="visit.business_name || ''">
                  {{ visit.business_name || '—' }}
                </span>

              </td>

              <!-- REMARK -->
              <td>

                <span class="remark-text" :title="visit.remark || ''">
                  {{ visit.remark || '—' }}
                </span>

              </td>

              <!-- DATE AND TIME - STOCK ALLOCATION FORMAT -->
              <td class="date-cell">
                {{ formatDate(visit.created_at) }}
              </td>

              <!-- VISITED BY -->
              <td class="visited-cell" :title="visit.visited || ''">
                {{ visit.visited || '—' }}
              </td>

              <!-- ACTIONS - SAME EYE ICON AS STOCK ALLOCATION -->
              <td class="details-cell">

                <button type="button" class="visit-action-icon"
                  :title="`View details - ${visit.business_name || 'Dealer Visit'}`"
                  :aria-label="`View details for ${visit.business_name || 'dealer'}`" @click="openVisitDetails(visit)">
                  <i class="fa fa-eye action-icon" aria-hidden="true"></i>
                </button>

              </td>

            </tr>

          </tbody>

        </table>

      </div>

      <!-- ================================= -->
      <!-- PAGINATION                       -->
      <!-- ================================= -->

      <div class="report-footer">

        <span>
          Showing
          {{ (safePage - 1) * pageSize + 1 }}–{{
            Math.min(
              safePage * pageSize,
              preparedVisits.length
            )
          }}
          of {{ preparedVisits.length }} visits
        </span>

        <div v-if="totalPages > 1" class="pagination-controls">

          <button type="button" :disabled="safePage === 1" aria-label="Previous page" @click="page = safePage - 1">
            <i class="fa fa-chevron-left"></i>
          </button>

          <span>
            {{ safePage }} / {{ totalPages }}
          </span>

          <button type="button" :disabled="safePage === totalPages" aria-label="Next page" @click="page = safePage + 1">
            <i class="fa fa-chevron-right"></i>
          </button>

        </div>

      </div>

    </section>

    <!-- ================================= -->
    <!-- EMPTY STATES                     -->
    <!-- ================================= -->

    <div v-else-if="searchedOnce" class="report-message">
      No data found. Please change filters and click Search.
    </div>

    <div v-else class="report-message">
      Please select filters and click Search.
    </div>

    <!-- ================================= -->
    <!-- NEW DEALER VISIT DETAILS POPUP    -->
    <!-- ================================= -->

    <div v-if="detailsVisit" class="visit-details-backdrop" @click.self="closeVisitDetails">

      <section ref="detailsDialog" class="visit-details-dialog" role="dialog" aria-modal="true"
        aria-labelledby="visit-details-title" tabindex="-1" @keydown.tab="onDetailsTab">

        <!-- ================================= -->
        <!-- POPUP HEADER                     -->
        <!-- ================================= -->

        <header class="visit-details-header">

          <div class="visit-details-heading">

            <h3 id="visit-details-title">
              Dealer Visit Details
            </h3>

            <p>
              {{ detailsVisit.business_name || 'Dealer' }}
            </p>

          </div>

          <button ref="detailsClose" type="button" class="visit-details-close" aria-label="Close dealer visit details"
            @click="closeVisitDetails">
            <i class="fa fa-times"></i>
          </button>

        </header>

        <!-- ================================= -->
        <!-- SCROLLABLE DETAILS CONTENT       -->
        <!-- ================================= -->

        <div class="visit-details-content">

          <!-- ================================= -->
          <!-- VISIT INFORMATION                -->
          <!-- ================================= -->

          <div class="visit-details-summary">

            <div class="visit-info-field">
              <span class="visit-info-label">
                Dealer
              </span>

              <strong>
                {{ detailsVisit.business_name || '—' }}
              </strong>
            </div>

            <div class="visit-info-field">
              <span class="visit-info-label">
                Visited By
              </span>

              <strong>
                {{ detailsVisit.visited || '—' }}
              </strong>
            </div>

            <div class="visit-info-field">
              <span class="visit-info-label">
                Visit Date
              </span>

              <strong>
                {{ datePart(detailsVisit.created_at) }}
              </strong>
            </div>

            <div class="visit-info-field">
              <span class="visit-info-label">
                Visit Time
              </span>

              <strong>
                {{ formatVisitTime(detailsVisit.created_at) }}
              </strong>
            </div>

            <div class="visit-info-field visit-info-full">
              <span class="visit-info-label">
                Remark
              </span>

              <span class="visit-info-remark">
                {{ detailsVisit.remark || '—' }}
              </span>
            </div>

            <div v-if="isInactiveDealer(detailsVisit)" class="visit-inactive-note visit-info-full">
              <i class="fa fa-info-circle"></i>
              This dealer is currently inactive.
            </div>

          </div>

          <!-- ================================= -->
          <!-- SECTION 1: CHECKLIST             -->
          <!-- ================================= -->

          <section class="visit-detail-section">

            <div class="visit-detail-section-heading">

              <h4>
                <i class="fa fa-check-square-o"></i>
                Checklist
              </h4>

              <span class="visit-section-count">
                {{ detailsChecklists.length }}
              </span>

            </div>

            <div v-if="detailsChecklists.length" class="visit-checklist-grid">

              <div v-for="(check, index) in detailsChecklists" :key="check.id ?? index" class="visit-checklist-entry">

                <span class="visit-checklist-name">
                  {{ check.checklists || 'Checklist Item' }}
                </span>

                <span :class="[
                  'visit-checklist-value',
                  {
                    'is-positive': String(check.list_value) === '1',
                    'is-negative': String(check.list_value) === '0'
                  }
                ]">

                  <template v-if="String(check.list_value) === '1'">
                    <i class="fa fa-check"></i>
                    Yes
                  </template>

                  <template v-else-if="String(check.list_value) === '0'">
                    <i class="fa fa-times"></i>
                    No
                  </template>

                  <template v-else>
                    —
                  </template>

                </span>

                <p v-if="check.details" class="visit-checklist-description">
                  {{ check.details }}
                </p>

              </div>

            </div>

            <div v-else class="visit-detail-empty">
              No checklist information available.
            </div>

          </section>

          <!-- ================================= -->
          <!-- SECTION 2: DEALER VISIT IMAGES   -->
          <!-- ================================= -->

          <section class="visit-detail-section">

            <div class="visit-detail-section-heading">

              <h4>
                <i class="fa fa-picture-o"></i>
                Dealer Visit Images
              </h4>

              <span class="visit-section-count">
                {{ detailsVisit.image_count || 0 }}
              </span>

            </div>

            <!-- IMAGES LOADING -->
            <div v-if="detailsImagesLoading" class="visit-detail-empty" role="status">
              <i class="fa fa-spinner fa-spin"></i>
              Loading visit images...
            </div>

            <!-- IMAGES ERROR -->
            <div v-else-if="detailsImagesError" class="visit-detail-empty">
              {{ detailsImagesError }}

              <button type="button" class="visit-image-retry" @click="loadVisitDetailImages(detailsVisit)">
                Retry
              </button>
            </div>

            <!-- IMAGE THUMBNAILS -->
            <div v-else-if="detailsImages.length" class="visit-images-grid">

              <button v-for="(photo, index) in detailsImages" :key="photo.id ?? index" type="button"
                class="visit-image-thumb" :aria-label="`Open visit image ${index + 1}`" @click="viewDetailImage(index)">

                <img :src="photo.src" :alt="`Dealer visit photo ${index + 1}`" loading="lazy" />

                <span class="visit-image-number">
                  {{ index + 1 }}
                </span>

              </button>

            </div>

            <!-- NO IMAGES -->
            <div v-else class="visit-detail-empty">
              No dealer visit images available.
            </div>

          </section>

          <!-- ================================= -->
          <!-- SECTION 3: ITEM QUANTITIES       -->
          <!-- ================================= -->

          <section class="visit-detail-section">

            <div class="visit-detail-section-heading">

              <h4>
                <i class="fa fa-list-alt"></i>
                Items
              </h4>

              <span class="visit-section-count">
                {{ detailsItems.length }}
              </span>

            </div>

            <div v-if="detailsItems.length" class="visit-items-table-wrap">

              <table class="visit-items-table">

                <thead>
                  <tr>
                    <th>Item Name</th>
                    <th>Sale Qty</th>
                    <th>Empty Qty</th>
                    <th>Refill Qty</th>
                  </tr>
                </thead>

                <tbody>

                  <tr v-for="(item, index) in detailsItems" :key="item.item_id ?? index">

                    <td>
                      {{ item.item_name || '—' }}
                    </td>

                    <td>
                      {{ formatQty(item.sale_qty) }}
                    </td>

                    <td>
                      {{ formatQty(item.empty_qty) }}
                    </td>

                    <td>
                      {{ formatQty(item.refil_qty) }}
                    </td>

                  </tr>

                </tbody>

              </table>

            </div>

            <div v-else class="visit-detail-empty">
              No item quantities available.
            </div>

          </section>

        </div>

        <!-- ================================= -->
        <!-- POPUP FOOTER                     -->
        <!-- ================================= -->

        <footer class="visit-details-footer">

          <button type="button" class="visit-details-done" @click="closeVisitDetails">
            Close
          </button>

        </footer>

      </section>

    </div>

    <!-- ================================= -->
    <!-- FULL-SCREEN IMAGE GALLERY         -->
    <!-- ================================= -->

    <div v-if="isGalleryOpen" class="gallery-backdrop" @click.self="closeVisitImages">

      <section ref="galleryDialog" class="gallery-dialog" role="dialog" aria-modal="true"
        aria-labelledby="gallery-title" tabindex="-1" @keydown.tab="onGalleryTab">

        <!-- GALLERY HEADER -->
        <header class="gallery-header">

          <div class="gallery-heading">

            <h3 id="gallery-title">
              Dealer Visit Images
            </h3>

            <p :title="galleryVisitName">
              {{ galleryVisitName }}
            </p>

          </div>

          <button ref="galleryClose" type="button" class="gallery-close" aria-label="Close image viewer"
            @click="closeVisitImages">
            <i class="fa fa-times"></i>
          </button>

        </header>

        <!-- LOADING -->
        <div v-if="galleryLoading" class="gallery-feedback" role="status">
          <i class="fa fa-spinner fa-spin"></i>
          Loading images...
        </div>

        <!-- ERROR -->
        <div v-else-if="galleryError" class="gallery-feedback error-text">
          {{ galleryError }}
        </div>

        <!-- IMAGE VIEWER -->
        <template v-else-if="galleryImages.length">

          <div class="gallery-stage">

            <button v-if="galleryImages.length > 1" type="button" class="stage-arrow previous"
              aria-label="Previous image" @click="previousVisitImage">
              <i class="fa fa-chevron-left"></i>
            </button>

            <img :key="galleryImages[galleryIndex].id ?? galleryIndex" :src="galleryImages[galleryIndex].src"
              :alt="`Dealer visit image ${galleryIndex + 1}`" />

            <button v-if="galleryImages.length > 1" type="button" class="stage-arrow next" aria-label="Next image"
              @click="nextVisitImage">
              <i class="fa fa-chevron-right"></i>
            </button>

          </div>

          <!-- GALLERY FOOTER -->
          <div class="gallery-bottom">

            <span class="gallery-position">
              Photo {{ galleryIndex + 1 }}
              of {{ galleryImages.length }}
            </span>

            <div v-if="galleryImages.length > 1" class="gallery-thumbnails" aria-label="Image thumbnails">

              <button v-for="(photo, index) in galleryImages" :key="photo.id ?? index" type="button" :class="[
                'gallery-thumb',
                { active: galleryIndex === index }
              ]" :aria-label="`Select photo ${index + 1}`" :aria-pressed="galleryIndex === index"
                @click="galleryIndex = index">
                <img :src="photo.src" :alt="`Photo ${index + 1} thumbnail`" />
              </button>

            </div>

          </div>

        </template>

        <!-- NO PHOTOS -->
        <div v-else class="gallery-feedback">
          No images available for this visit.
        </div>

      </section>

    </div>

  </div>
</template>

<script>
import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchDealerListManagementTrue,
  searchdealervisit,
  fetchDealerVisitImages
} from "@/services/dataService";

import { useToast } from "vue-toastification";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// =================================
// LOCAL DATE HELPER
// =================================

function localToday() {
  const d = new Date();

  return `${d.getFullYear()}-${String(
    d.getMonth() + 1
  ).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

export default {

  data() {
    const today = localToday();

    return {
      token: localStorage.getItem("token_set") || "",

      ALL: "__all__",

      // Filter lists
      fsmList: [],
      asmList: [],
      distributorList: [],
      dealerList: [],

      // Selected filters
      selectedFsm: "",
      selectedAsm: "",
      selectedDistributor: "",
      selectedDealer: "",

      // Date range
      startDate: today,
      endDate: today,

      // Report data
      complaintList: [],

      // User role
      userLabel: "",
      roleKey: "OTHER",

      myProfileId: Number(
        localStorage.getItem("profile_id") || 0
      ),

      isFsmLocked: false,
      isAsmLocked: false,
      isDistributorLocked: false,

      // Loading
      loadingInit: false,
      loading: false,
      searchedOnce: false,

      // Pagination
      page: 1,
      pageSize: 15,

    
      // NEW DETAILS POPUP
    

      detailsVisit: null,

      detailsImages: [],
      detailsImagesLoading: false,
      detailsImagesError: "",

      detailsRequestId: 0,

    
      // IMAGE GALLERY
    

      isGalleryOpen: false,

      galleryVisitName: "",
      galleryImages: [],
      galleryIndex: 0,

      galleryLoading: false,
      galleryError: "",

      galleryRequestId: 0,

      // Modal scroll/focus management
      originalBodyOverflow: "",
      detailsReturnFocus: null,
      galleryReturnFocus: null
    };
  },


  // LIFECYCLE


  created() {
    this.loginAndFetchData();
  },

  mounted() {
    window.addEventListener(
      "keydown",
      this.onGalleryKeydown
    );
  },

  beforeUnmount() {
    window.removeEventListener(
      "keydown",
      this.onGalleryKeydown
    );

    this.detailsRequestId++;
    this.galleryRequestId++;

    if (this.detailsVisit || this.isGalleryOpen) {
      document.body.style.overflow =
        this.originalBodyOverflow;
    }
  },


  // COMPUTED


  computed: {

  
    // NEW DETAILS POPUP DATA
  

    detailsChecklists() {
      return Array.isArray(
        this.detailsVisit?.dealerCheckListDetails
      )
        ? this.detailsVisit.dealerCheckListDetails
        : [];
    },

    detailsItems() {
      return Array.isArray(
        this.detailsVisit?.dealerVisititems
      )
        ? this.detailsVisit.dealerVisititems
        : [];
    },

  
    // EXISTING COMPUTED PROPERTIES
  

    currentDate() {
      return localToday();
    },

    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    isAsmUser() {
      return this.roleKey === "ASM";
    },

    isFsmUser() {
      return this.roleKey === "FSM";
    },

    isOtherUser() {
      return this.roleKey === "OTHER";
    },

    isLoading() {
      return this.loadingInit || this.loading;
    },

    canUseDealerFilter() {
      return (
        !!this.selectedDistributor &&
        this.selectedDistributor !== this.ALL
      );
    },

  
    // FILTERED REPORT DATA
    // UNCHANGED
  

    filteredComplaintList() {
      if (!Array.isArray(this.complaintList)) {
        return [];
      }

      return this.complaintList.filter(visit => {
        const date = String(
          visit.created_at || ""
        ).slice(0, 10);

        return (
          (!this.startDate || date >= this.startDate) &&
          (!this.endDate || date <= this.endDate)
        );
      });
    },

  
    // DATA PREPARATION
    // UNCHANGED FOR EXCEL EXPORT
  

    preparedVisits() {
      return this.filteredComplaintList.map(visit => {
        const checks = Object.create(null);
        const items = Object.create(null);

        (visit.dealerCheckListDetails || []).forEach(c => {
          checks[c.checklists] = String(
            c.list_value ?? ""
          );
        });

        (visit.dealerVisititems || []).forEach(i => {
          items[String(i.item_id)] = i;
        });

        return {
          ...visit,
          _checks: checks,
          _items: items
        };
      });
    },

  
    // UNIQUE CHECKLISTS
    // KEEP FOR EXCEL EXPORT
  

    uniqueChecklists() {
      const names = new Set();

      this.filteredComplaintList.forEach(visit => {
        (visit.dealerCheckListDetails || []).forEach(c => {
          names.add(c.checklists);
        });
      });

      return Array.from(
        names,
        name => ({ name })
      );
    },

  
    // UNIQUE ITEMS
    // KEEP FOR EXCEL EXPORT
  

    uniqueItems() {
      const items = new Map();

      this.filteredComplaintList.forEach(visit => {
        (visit.dealerVisititems || []).forEach(i => {
          const id = String(i.item_id);

          if (!items.has(id)) {
            items.set(id, {
              id: i.item_id,
              name: i.item_name,
              hasQuantity: false
            });
          }

          if (
            [
              i.sale_qty,
              i.empty_qty,
              i.refil_qty
            ].some(
              qty => (Number(qty) || 0) > 0
            )
          ) {
            items.get(id).hasQuantity = true;
          }
        });
      });

      return Array.from(
        items.values()
      ).filter(item => item.hasQuantity);
    },

  
    // TOTAL IMAGES
  

    totalImages() {
      return this.filteredComplaintList.reduce(
        (sum, visit) =>
          sum + (Number(visit.image_count) || 0),
        0
      );
    },

  
    // PAGINATION
  

    totalPages() {
      return Math.max(
        1,
        Math.ceil(
          this.preparedVisits.length / this.pageSize
        )
      );
    },

    safePage() {
      return Math.min(
        Math.max(1, this.page),
        this.totalPages
      );
    },

    pageVisits() {
      const start =
        (this.safePage - 1) * this.pageSize;

      return this.preparedVisits.slice(
        start,
        start + this.pageSize
      );
    }
  },


  // METHODS


  methods: {

  
    // VISIT TIME - 12 HOUR AM/PM FORMAT
  

    formatVisitTime(value) {
      if (!value) return "—";

      const time = String(value).match(
        /(?:T|\s)(\d{2}):(\d{2})/
      );

      if (!time) return "—";

      let hours = Number(time[1]);
      const minutes = time[2];

      if (hours < 0 || hours > 23) return "—";

      const period = hours >= 12 ? "PM" : "AM";

      hours = hours % 12 || 12;

      return `${String(hours).padStart(2, "0")}:${minutes} ${period}`;
    },

  
    // QUANTITY FORMATTING - UI & EXCEL
  

    formatQty(value, emptyValue = "—") {
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
      ) {
        return emptyValue;
      }

      const quantity = Number(value);

      if (!Number.isFinite(quantity)) {
        return emptyValue;
      }

      // 0.00 -> 0
      // 2.00 -> 2
      // 2.50 -> 2.5
      // Keep genuine decimal quantities.
      return quantity;
    },

  
    // INACTIVE DEALER DETECTION
  

    isInactiveDealer(dealer) {
      if (!dealer) {
        return false;
      }

      return (
        dealer.is_active === false ||
        String(dealer.is_active) === "0"
      );
    },

  
    // TOAST
  

    toast(type, message) {
      const t = useToast();

      t[
        type === "error"
          ? "error"
          : "success"
      ](message, {
        position: "top-right",
        timeout: 3000
      });
    },

    showErrorToast(message) {
      this.toast("error", message);
    },

    showSuccessToast(message) {
      this.toast("success", message);
    },

  
    // USER ROLE - EXISTING LOGIC
  

    initRoleFromStorage() {
      const label =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(label);

      const role = this.userLabel
        .toUpperCase()
        .trim();

      if (
        role === "DISTRIBUTOR" ||
        role.startsWith("DISTRIBUTOR") ||
        role === "DIS" ||
        role.startsWith("DIS_")
      ) {
        this.roleKey = "DIS";
      } else if (
        role === "ASM" ||
        role.startsWith("ASM_")
      ) {
        this.roleKey = "ASM";
      } else if (
        role === "FSM" ||
        role.startsWith("FSM_")
      ) {
        this.roleKey = "FSM";
      } else {
        this.roleKey = "OTHER";
      }

      this.myProfileId = Number(
        localStorage.getItem("profile_id") || 0
      );

      this.isFsmLocked = false;
      this.isAsmLocked = false;
      this.isDistributorLocked = false;
    },

  
    // FILTER VALIDATION
  

    validateFiltersOneByOne() {
      if (!this.selectedFsm) {
        this.showErrorToast("Please select FSM");
        return false;
      }

      if (!this.selectedAsm) {
        this.showErrorToast("Please select ASM");
        return false;
      }

      if (
        !this.selectedDistributor ||
        (
          this.roleKey === "DIS" &&
          this.selectedDistributor === this.ALL
        )
      ) {
        this.showErrorToast(
          "Please select Distributor"
        );
        return false;
      }

      return true;
    },

    isValidISODate(value) {
      const match =
        typeof value === "string"
          ? value.match(
            /^(\d{4})-(\d{2})-(\d{2})$/
          )
          : null;

      if (!match) {
        return false;
      }

      const date = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
      );

      return (
        date.getFullYear() === Number(match[1]) &&
        date.getMonth() + 1 === Number(match[2]) &&
        date.getDate() === Number(match[3])
      );
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) {
        return true;
      }

      if (
        !this.isValidISODate(this.startDate) ||
        this.startDate > this.currentDate
      ) {
        if (showToast) {
          this.showErrorToast(
            this.startDate > this.currentDate
              ? "Start Date cannot be in the future"
              : "Invalid Start Date"
          );
        }

        this.startDate = "";
        return false;
      }

      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) {
        return true;
      }

      if (
        !this.isValidISODate(this.endDate) ||
        this.endDate > this.currentDate
      ) {
        if (showToast) {
          this.showErrorToast(
            this.endDate > this.currentDate
              ? "End Date cannot be in the future"
              : "Invalid End Date"
          );
        }

        this.endDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      this.validateStartDate(false);
      this.validateEndDate(false);

      if (
        this.startDate &&
        this.endDate &&
        this.endDate < this.startDate
      ) {
        this.showErrorToast(
          "Invalid date range picked."
        );

        this.endDate = "";
      }
    },

    validateDatesForSearch() {
      if (
        !this.startDate ||
        !this.endDate
      ) {
        this.showErrorToast(
          "Please select valid Start Date and End Date"
        );

        return false;
      }

      if (
        !this.validateStartDate(true) ||
        !this.validateEndDate(true)
      ) {
        return false;
      }

      if (this.endDate < this.startDate) {
        this.showErrorToast(
          "Invalid date range picked."
        );

        return false;
      }

      return true;
    },

  
    // FETCH FILTER DATA
  

    async loginAndFetchData() {
      this.loadingInit = true;

      try {
        this.initRoleFromStorage();

        this.token =
          localStorage.getItem("token_set") || "";

        await this.loadFsmList();

      } catch (error) {
        console.error(
          "Error fetching data:",
          error
        );

      } finally {
        this.loadingInit = false;
      }
    },

    async loadFsmList() {
      const id =
        localStorage.getItem("profile_id");

      if (!id) {
        console.error(
          "Profile ID not found in localStorage"
        );
        return;
      }

      try {
        const response = await fetchFsmList(
          id,
          this.token
        );

        this.fsmList =
          response.data.data || [];

        this.selectedFsm = "";
        this.selectedAsm = "";
        this.selectedDistributor = "";
        this.selectedDealer = "";

        this.asmList = [];
        this.distributorList = [];
        this.dealerList = [];

        this.isDistributorLocked = false;

      } catch (error) {
        console.error(
          "Error loading FSM list:",
          error
        );
      }
    },

    async onFsmChange() {
      this.selectedAsm = "";
      this.selectedDistributor = "";
      this.selectedDealer = "";

      this.asmList = [];
      this.distributorList = [];
      this.dealerList = [];

      this.isDistributorLocked = false;

      if (!this.selectedFsm) {
        return;
      }

      if (this.selectedFsm === this.ALL) {
        this.selectedAsm = this.ALL;
        this.selectedDistributor = this.ALL;
        return;
      }

      await this.loadAsmList();
    },

    async loadAsmList() {
      if (
        !this.selectedFsm ||
        this.selectedFsm === this.ALL
      ) {
        this.asmList = [];
        this.selectedAsm = "";
        this.selectedDistributor = "";
        this.selectedDealer = "";
        return;
      }

      try {
        const id =
          localStorage.getItem("profile_id");

        const response = await fetchAsmList(
          id,
          this.selectedFsm,
          this.token
        );

        this.asmList =
          response.data.data || [];

        this.selectedAsm = "";
        this.selectedDistributor = "";
        this.selectedDealer = "";

        this.distributorList = [];
        this.dealerList = [];

        this.isDistributorLocked = false;

      } catch (error) {
        console.error(
          "Error loading ASM list:",
          error
        );
      }
    },

    async onAsmChange() {
      this.selectedDistributor = "";
      this.selectedDealer = "";

      this.distributorList = [];
      this.dealerList = [];

      this.isDistributorLocked = false;

      if (!this.selectedAsm) {
        return;
      }

      if (this.selectedAsm === this.ALL) {
        this.selectedDistributor = this.ALL;
        return;
      }

      await this.loadDistributorList();
    },

    async loadDistributorList() {
      this.selectedDistributor = "";
      this.selectedDealer = "";

      this.distributorList = [];
      this.dealerList = [];

      this.isDistributorLocked = false;

      if (!this.selectedAsm) {
        return;
      }

      if (this.selectedAsm === this.ALL) {
        this.selectedDistributor = this.ALL;
        return;
      }

      try {
        const response = await fetchDistributorList(
          this.selectedFsm,
          this.selectedAsm,
          this.token
        );

        let list =
          response.data.data || [];

        if (
          this.roleKey === "DIS" &&
          this.myProfileId
        ) {
          const mine = list.find(
            d =>
              Number(d.profile_id) ===
              this.myProfileId
          );

          if (mine) {
            list = [mine];

            this.selectedDistributor =
              String(mine.profile_id);

            this.isDistributorLocked = true;

            await this.loadDealerList();
          }
        }

        this.distributorList = list;

      } catch (error) {
        console.error(
          "Error loading Distributor list:",
          error
        );

        this.distributorList = [];
        this.selectedDistributor = "";
        this.selectedDealer = "";

        this.isDistributorLocked = false;
      }
    },

    async onDistributorChange() {
      this.selectedDealer = "";
      this.dealerList = [];

      if (
        !this.selectedDistributor ||
        this.selectedDistributor === this.ALL
      ) {
        return;
      }

      await this.loadDealerList();
    },

    async loadDealerList() {
      if (
        !this.selectedDistributor ||
        this.selectedDistributor === this.ALL
      ) {
        this.dealerList = [];
        this.selectedDealer = "";
        return;
      }

      await this.loadDealerListManagement();
    },

    async loadDealerListManagement() {
      if (
        !this.selectedDistributor ||
        this.selectedDistributor === this.ALL
      ) {
        this.dealerList = [];
        this.selectedDealer = "";
        return;
      }

      try {
        const id =
          localStorage.getItem("profile_id");

        // Includes active and inactive dealers.
        const response =
          await fetchDealerListManagementTrue(
            id,
            this.selectedDistributor,
            true,
            this.token
          );

        this.dealerList = Array.isArray(
          response?.data?.data
        )
          ? response.data.data
          : [];

        this.selectedDealer = "";

      } catch (error) {
        console.error(
          "Error loading dealer list:",
          error
        );

        this.dealerList = [];
        this.selectedDealer = "";
      }
    },

  
    // SEARCH
  

    async performSearch() {
      if (
        !this.validateFiltersOneByOne() ||
        !this.validateDatesForSearch()
      ) {
        return;
      }

      this.loading = true;
      this.searchedOnce = true;
      this.complaintList = [];
      this.page = 1;

      // Close previously opened popups.
      this.closeVisitImages();
      this.closeVisitDetails();

      const payload = {
        fsm_id:
          this.selectedFsm &&
            this.selectedFsm !== this.ALL
            ? this.selectedFsm
            : null,

        asm_id:
          this.selectedAsm &&
            this.selectedAsm !== this.ALL
            ? this.selectedAsm
            : null,

        distributor_id:
          this.selectedDistributor &&
            this.selectedDistributor !== this.ALL
            ? this.selectedDistributor
            : null,

        dealer_id:
          this.selectedDealer || null,

        start_date: this.startDate,
        end_date: this.endDate
      };

      try {
        const response = await searchdealervisit(
          payload,
          this.token
        );

        this.complaintList = Array.isArray(
          response?.data?.data
        )
          ? response.data.data
          : [];

        if (this.complaintList.length) {
          this.showSuccessToast(
            "Report Generated Successfully"
          );
        } else {
          this.showErrorToast(
            "No data found for selected filters."
          );
        }

      } catch (error) {
        console.error(
          "Error searching dealer visits:",
          error
        );

        this.showErrorToast(
          error?.response?.data?.message ||
          "Search failed."
        );

      } finally {
        this.loading = false;
      }
    },

  
    // STOCK ALLOCATION DATE TIME FORMAT
    // YYYY-MM-DD hh:mm AM/PM
  

    formatDate(dateStr) {
      if (
        !dateStr ||
        String(dateStr).startsWith("1900-01-01") ||
        String(dateStr).startsWith("0000-00-00")
      ) {
        return "-";
      }

      const formattedStr =
        dateStr.includes(" ") && !dateStr.includes("T")
          ? dateStr.replace(" ", "T")
          : dateStr;

      const date = new Date(formattedStr);

      if (isNaN(date.getTime())) {
        return dateStr;
      }

      const year = date.getFullYear();

      const month = String(
        date.getMonth() + 1
      ).padStart(2, "0");

      const day = String(
        date.getDate()
      ).padStart(2, "0");

      let hours = date.getHours();

      const minutes = String(
        date.getMinutes()
      ).padStart(2, "0");

      const ampm = hours >= 12 ? "PM" : "AM";

      hours = hours % 12;
      hours = hours ? hours : 12;

      const formattedHours = String(
        hours
      ).padStart(2, "0");

      return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
    },

  
    // DATE DISPLAY HELPERS
  

    datePart(value) {
      return (
        String(value || "").slice(0, 10) || "—"
      );
    },

    timePart(value) {
      return (
        String(value || "").slice(11, 16) || ""
      );
    },

  
    // BODY SCROLL MANAGEMENT
  

    lockModalScroll() {
      if (!this.detailsVisit && !this.isGalleryOpen) {
        this.originalBodyOverflow =
          document.body.style.overflow;
      }

      document.body.style.overflow = "hidden";
    },

    restoreModalScroll() {
      if (!this.detailsVisit && !this.isGalleryOpen) {
        document.body.style.overflow =
          this.originalBodyOverflow;
      }
    },

  
    // FOCUS TRAP FOR POPUPS
  

    trapPopupFocus(event, dialog) {
      if (!dialog) return;

      const elements = Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), ' +
          'a[href], input:not([disabled]), ' +
          'select:not([disabled]), ' +
          '[tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        el => el.getClientRects().length > 0
      );

      if (!elements.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    },

    onDetailsTab(event) {
      this.trapPopupFocus(
        event,
        this.$refs.detailsDialog
      );
    },

    onGalleryTab(event) {
      this.trapPopupFocus(
        event,
        this.$refs.galleryDialog
      );
    },

  
    // NEW DEALER VISIT DETAILS POPUP
  

    openVisitDetails(visit) {
      if (!visit) return;

      if (!this.detailsVisit && !this.isGalleryOpen) {
        this.detailsReturnFocus =
          document.activeElement;

        this.lockModalScroll();
      }

      this.detailsRequestId++;

      // Use the selected report row.
      this.detailsVisit = visit;

      this.detailsImages = [];
      this.detailsImagesLoading = false;
      this.detailsImagesError = "";

      this.$nextTick(() => {
        this.$refs.detailsClose?.focus();
      });

      // Fetch only the images separately.
      this.loadVisitDetailImages(visit);
    },

    closeVisitDetails() {
      const hadDetails = !!this.detailsVisit;

      this.detailsRequestId++;

      this.detailsVisit = null;
      this.detailsImages = [];
      this.detailsImagesLoading = false;
      this.detailsImagesError = "";

      this.restoreModalScroll();

      if (hadDetails && !this.isGalleryOpen) {
        this.$nextTick(() => {
          if (this.detailsReturnFocus?.isConnected) {
            this.detailsReturnFocus.focus();
          }

          this.detailsReturnFocus = null;
        });
      }
    },

  
    // LOAD IMAGES FOR DETAILS POPUP
  

    async loadVisitDetailImages(visit) {
      if (!visit || visit.id == null) {
        return;
      }

      const requestId =
        ++this.detailsRequestId;

      this.detailsImages = [];
      this.detailsImagesError = "";
      this.detailsImagesLoading = false;

      // Avoid unnecessary API calls.
      if (Number(visit.image_count) <= 0) {
        return;
      }

      this.detailsImagesLoading = true;

      try {
        const response =
          await fetchDealerVisitImages(
            visit.id,
            this.token
          );

        // Ignore an old response if the popup
        // was closed or a different visit opened.
        if (
          requestId !== this.detailsRequestId
        ) {
          return;
        }

        const images =
          response?.data?.images ??
          response?.data?.data?.images ??
          [];

        if (!Array.isArray(images)) {
          throw new Error(
            "Invalid visit images response"
          );
        }

        this.detailsImages = images
          .map(image => ({
            id: image.id,
            src: this.toDealerVisitImageSrc(
              image.image
            )
          }))
          .filter(image => image.src);

      } catch (error) {
        if (
          requestId !== this.detailsRequestId
        ) {
          return;
        }

        console.error(
          "Failed to load dealer visit images:",
          error
        );

        this.detailsImagesError =
          "Unable to load dealer visit images.";

      } finally {
        if (
          requestId === this.detailsRequestId
        ) {
          this.detailsImagesLoading = false;
        }
      }
    },

  
    // IMAGE ENCODING
    // EXISTING LOGIC
  

    toDealerVisitImageSrc(value) {
      if (typeof value !== "string") {
        return "";
      }

      const raw = value.trim();

      if (
        /^data:image\/(?:jpeg|jpg|png|gif|webp);base64,[a-z0-9+/=\s]+$/i.test(
          raw
        )
      ) {
        return raw;
      }

      if (
        !raw ||
        !/^[a-z0-9+/=\s]+$/i.test(raw)
      ) {
        return "";
      }

      const b64 = raw.replace(/\s/g, "");

      const mime =
        b64.startsWith("iVBOR")
          ? "image/png"
          : b64.startsWith("R0lGOD")
            ? "image/gif"
            : b64.startsWith("UklGR")
              ? "image/webp"
              : "image/jpeg";

      return `data:${mime};base64,${b64}`;
    },

  
    // OPEN FULL-SCREEN IMAGE VIEWER
    // FROM DETAILS POPUP
  

    viewDetailImage(index) {
      if (!this.detailsImages.length) {
        return;
      }

      this.galleryReturnFocus =
        document.activeElement;

      if (!this.detailsVisit && !this.isGalleryOpen) {
        this.lockModalScroll();
      }

      this.galleryRequestId++;

      this.galleryVisitName =
        this.detailsVisit?.business_name ||
        "Dealer Visit";

      // Reuse already loaded images.
      // No second API call.
      this.galleryImages = [
        ...this.detailsImages
      ];

      this.galleryIndex = Math.max(
        0,
        Math.min(
          index,
          this.galleryImages.length - 1
        )
      );

      this.galleryError = "";
      this.galleryLoading = false;
      this.isGalleryOpen = true;

      this.$nextTick(() => {
        this.$refs.galleryClose?.focus();
      });
    },

  
    // CLOSE IMAGE VIEWER
  

    closeVisitImages() {
      const wasOpen = this.isGalleryOpen;

      this.galleryRequestId++;

      this.isGalleryOpen = false;
      this.galleryImages = [];
      this.galleryIndex = 0;
      this.galleryLoading = false;
      this.galleryError = "";

      this.restoreModalScroll();

      if (wasOpen && this.detailsVisit) {
        this.$nextTick(() => {
          if (this.galleryReturnFocus?.isConnected) {
            this.galleryReturnFocus.focus();
          } else {
            this.$refs.detailsClose?.focus();
          }

          this.galleryReturnFocus = null;
        });
      } else {
        this.galleryReturnFocus = null;
      }
    },

  
    // IMAGE NAVIGATION
  

    previousVisitImage() {
      if (this.galleryImages.length) {
        this.galleryIndex =
          (
            this.galleryIndex -
            1 +
            this.galleryImages.length
          ) %
          this.galleryImages.length;
      }
    },

    nextVisitImage() {
      if (this.galleryImages.length) {
        this.galleryIndex =
          (
            this.galleryIndex + 1
          ) %
          this.galleryImages.length;
      }
    },

  
    // ESCAPE AND ARROW KEY HANDLING
  

    onGalleryKeydown(event) {
      // Gallery has priority over details popup.
      if (this.isGalleryOpen) {
        if (event.key === "Escape") {
          event.preventDefault();
          this.closeVisitImages();

        } else if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.previousVisitImage();

        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          this.nextVisitImage();
        }

        return;
      }

      // Escape closes the details popup.
      if (
        this.detailsVisit &&
        event.key === "Escape"
      ) {
        event.preventDefault();
        this.closeVisitDetails();
      }
    },

  
    // EXCEL HELPERS
    // UNCHANGED
  

    getName(list, id) {
      if (!id || id === this.ALL) {
        return "All";
      }

      const found = list.find(
        i =>
          String(i.profile_id) ===
          String(id)
      );

      return found
        ? found.name
        : "";
    },

  
    // EXCEL EXPORT
    // EXISTING FORMAT UNCHANGED
  

    exportToExcel() {
      if (!this.preparedVisits.length) {
        return;
      }

      const filterData = [
        ["Dealer Visit Report"],
        [],

        [
          "FSM",
          this.getName(
            this.fsmList,
            this.selectedFsm
          )
        ],

        [
          "ASM",
          this.getName(
            this.asmList,
            this.selectedAsm
          )
        ],

        [
          "Distributor",
          this.getName(
            this.distributorList,
            this.selectedDistributor
          )
        ],

        [
          "Dealer",
          this.getName(
            this.dealerList,
            this.selectedDealer
          )
        ],

        [
          "Start Date",
          this.startDate
        ],

        [
          "End Date",
          this.endDate
        ],

        []
      ];

      const tableData = [];

      // Export all matching visits,
      // not only the visible page.

      this.preparedVisits.forEach(
        visit => {
          [
            "Sale Qty",
            "Empty Qty",
            "Refill Qty"
          ].forEach(type => {
            const row = {
              "Dealer Name":
                visit.business_name,

              "Remark":
                visit.remark,

              "Date | Time":
                visit.created_at,

              "Visited by":
                visit.visited
            };

            this.uniqueChecklists.forEach(
              c => {
                const v =
                  visit._checks[c.name];

                row[c.name] =
                  v === "1"
                    ? "Yes"
                    : v === "0"
                      ? "No"
                      : "-";
              }
            );

            row["Dealer Visit Images"] =
              Number(
                visit.image_count
              ) || 0;

            row["Type"] = type;

            this.uniqueItems.forEach(i => {
              const obj = visit._items[String(i.id)];

              const rawQty =
                type === "Sale Qty"
                  ? obj?.sale_qty
                  : type === "Empty Qty"
                    ? obj?.empty_qty
                    : obj?.refil_qty;

              row[i.name] = this.formatQty(rawQty, "-");
            });

            tableData.push(row);
          });
        }
      );

      const ws =
        XLSX.utils.aoa_to_sheet(
          filterData
        );

      XLSX.utils.sheet_add_json(
        ws,
        tableData,
        {
          origin: "A10"
        }
      );

      const generatedBy =
        `${localStorage.getItem(
          "first_name"
        ) || ""
          } ${localStorage.getItem(
            "last_name"
          ) || ""
          }`.trim() || "-";

      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [],
          [
            "Generated By",
            generatedBy
          ],
          [
            "Generated At",
            new Date().toLocaleString()
          ]
        ],
        {
          origin:
            `A${10 +
            tableData.length +
            1
            }`
        }
      );

      const wb =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        wb,
        ws,
        "Dealer Visit Report"
      );

      const output =
        XLSX.write(
          wb,
          {
            bookType: "xlsx",
            type: "array"
          }
        );

      saveAs(
        new Blob(
          [output],
          {
            type:
              "application/octet-stream"
          }
        ),
        `DealerVisitReport_${localToday()}.xlsx`
      );
    }

  }
};
</script>

<style scoped>
/* ================================= */
/* BASE PAGE                         */
/* ================================= */

.dealer-report-page {
  width: 100%;
  min-width: 0;
  padding: 12px;
  background: #fff;
  box-sizing: border-box;
}

/* ================================= */
/* EXISTING FOUR-COLUMN FILTERS      */
/* UNCHANGED                         */
/* ================================= */

/* ================================= */
/* SEARCH AND EXPORT BUTTON LAYOUT   */
/* ================================= */

/* ================================= */
/* DAY END REPORT BUTTON STYLING     */
/* ================================= */

.dealer-report-page .search-container .btn {
  height: 40px;
  padding: 0 18px;
  margin: 0 !important;

  border: none;
  border-radius: 5px;

  font-size: 14px;
  font-weight: 500;
  line-height: normal;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  white-space: nowrap;
  box-sizing: border-box;
  cursor: pointer;

  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;
}

/* SEARCH */

.dealer-report-page .search-container .btn.button-default {
  background-color: #007bff !important;
  border: none !important;
  color: #ffffff !important;
}

.dealer-report-page .search-container .btn.button-default:hover:not(:disabled) {
  background-color: #0056b3 !important;
  color: #ffffff !important;
}

/* EXCEL */

.dealer-report-page .search-container .btn.button-export {
  background-color: #28a745 !important;
  border: none !important;
  color: #ffffff !important;
}

.dealer-report-page .search-container .btn.button-export:hover:not(:disabled) {
  background-color: #218838 !important;
  color: #ffffff !important;
}

/* DISABLED */

.dealer-report-page .search-container .btn:disabled,
.dealer-report-page .search-container .btn.button-default:disabled,
.dealer-report-page .search-container .btn.button-export:disabled {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  color: #ffffff !important;

  cursor: not-allowed;
  opacity: 0.7;
}

/* FOCUS */

.dealer-report-page .search-container .btn:focus-visible {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* ================================= */
/* REPORT MESSAGES                   */
/* ================================= */

.report-message {
  width: 100%;
  padding: 26px 10px;
  text-align: center;
  color: #667085;
  font-size: 14px;
}

/* ================================= */
/* REPORT CARD                       */
/* ================================= */

.report-card {
  width: 100%;
  min-width: 0;
  border: 1px solid #e2e6ea;
  border-radius: 9px;
  background: #fff;
  overflow: hidden;
}

/* ================================= */
/* REPORT TOOLBAR                    */
/* ================================= */

.report-toolbar {
  padding: 11px 14px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e8ebf0;
}

.report-toolbar-heading {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
}

.report-toolbar h3 {
  font-size: 15px;
  line-height: 1.3;
  margin: 0;
  font-weight: 700;
  color: #263445;
}

.report-subtitle,
.report-range {
  color: #667085;
  font-size: 12px;
}

.report-dot {
  margin: 0 3px;
}

/* ================================= */
/* REPORT TABLE WRAPPER              */
/* ================================= */

.report-table-wrap {
  width: 100%;
  overflow-x: auto;
}

/* ================================= */
/* REPORT TABLE                      */
/* ================================= */

.report-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
  font-size: 12px;
  color: #344054;
}

.report-table th,
.report-table td {
  border-right: 1px solid #edf0f4;
  border-bottom: 1px solid #edf0f4;
  vertical-align: middle;
  padding: 7px 9px;
}

.report-table th:last-child,
.report-table td:last-child {
  border-right: none;
}

.report-table thead th {
  text-align: center;
  background: #f5f7fa;
  font-weight: 600;
  color: #475467;
  line-height: 1.25;
}

/* ================================= */
/* SUMMARY TABLE                     */
/* ================================= */

.dealer-summary-table {
  width: 100%;
  table-layout: auto;
}

.dealer-summary-table thead th {
  padding: 12px 14px;
  white-space: nowrap;
}

.dealer-summary-table tbody td {
  padding: 11px 14px;
}

.dealer-summary-table .dealer-col {
  width: 24%;
  min-width: 145px;
  text-align: left;
}

.dealer-summary-table .remark-col {
  width: 30%;
  min-width: 135px;
  text-align: left;
}

.dealer-summary-table .date-col {
  width: 16%;
  min-width: 104px;
}

.dealer-summary-table .visited-col {
  width: 18%;
  min-width: 95px;
}

.dealer-summary-table .details-col {
  width: 12%;
  min-width: 130px;
}

.dealer-summary-table .details-cell {
  text-align: center;
  white-space: nowrap;
}

/* ================================= */
/* ROW STATES                        */
/* ================================= */

.report-table tbody tr:nth-child(even) {
  background-color: #fafbfc;
}

.report-table tbody tr:hover {
  background-color: #f4f8ff;
}

/* Inactive dealer - light red */

.report-table tbody tr.inactive-dealer-row,
.report-table tbody tr.inactive-dealer-row>td {
  background-color: #ffebee !important;
}

/* Inactive hover */

.report-table tbody tr.inactive-dealer-row:hover,
.report-table tbody tr.inactive-dealer-row:hover>td {
  background-color: #ffdcdb !important;
}

.report-table tbody tr:last-child td {
  border-bottom: 0;
}

/* ================================= */
/* DEALER NAME                       */
/* ================================= */

.dealer-name {
  display: block;
  max-width: 240px;
  font-size: 12px;
  font-weight: 600;
  color: #25334a;
  overflow-wrap: anywhere;
}

/* ================================= */
/* REMARK                            */
/* ================================= */

.remark-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;

  max-width: 350px;
  overflow: hidden;
  white-space: normal;

  color: #667085;
  line-height: 1.4;
}

/* ================================= */
/* DATE | TIME - SINGLE LINE         */
/* ================================= */

.dealer-summary-table .date-col {
  width: 20%;
  min-width: 180px;
  white-space: nowrap;
}

.dealer-summary-table .date-cell {
  white-space: nowrap;

  color: #344054;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.visited-cell {
  max-width: 180px;
  overflow-wrap: anywhere;
}

/* ================================= */
/* ACTIONS - STOCK ALLOCATION STYLE  */
/* ================================= */

.dealer-summary-table .details-col {
  width: 8%;
  min-width: 75px;
  text-align: center;
}

.dealer-summary-table .details-cell {
  text-align: center;
  white-space: nowrap;
}

/* Standalone eye icon button */

.visit-action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 30px;
  height: 30px;
  padding: 0;

  background: transparent;
  border: none;
  border-radius: 4px;

  color: inherit;
  cursor: pointer;
}

/* Same eye icon behavior as Stock Allocation */

.visit-action-icon .fa-eye {
  cursor: pointer;

  font-size: 15px;
  margin-left: 5px;

  transition:
    color 0.3s,
    transform 0.3s;
}

.visit-action-icon:hover .fa-eye {
  color: #0056b3;
  transform: scale(1.2);
}

.visit-action-icon:active .fa-eye {
  transform: scale(1.1);
}

.visit-action-icon:focus-visible {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* ================================= */
/* REPORT FOOTER                     */
/* ================================= */

.report-footer {
  padding: 9px 13px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  border-top: 1px solid #edf0f4;

  color: #667085;
  font-size: 12px;
}

/* ================================= */
/* PAGINATION                        */
/* ================================= */

.pagination-controls {
  display: flex;
  gap: 9px;
  align-items: center;
}

.pagination-controls button {
  width: 27px;
  height: 27px;

  background: #fff;
  color: #344054;

  border: 1px solid #d9e1e8;
  border-radius: 5px;
  cursor: pointer;
}

.pagination-controls button:disabled {
  color: #bfc5ce;
  cursor: default;
  background: #f8f9fb;
}

/* ================================= */
/* DETAILS POPUP BACKDROP            */
/* ================================= */

.visit-details-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2100;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 18px;

  background: rgba(15, 23, 42, 0.65);
}

/* ================================= */
/* DETAILS DIALOG                    */
/* ================================= */

.visit-details-dialog {
  width: min(920px, 100%);
  max-height: calc(100dvh - 36px);

  display: flex;
  flex-direction: column;

  min-height: 0;
  overflow: hidden;

  background: #ffffff;
  border-radius: 11px;

  box-shadow: 0 20px 55px rgba(0, 0, 0, 0.25);
}

/* ================================= */
/* DETAILS HEADER                    */
/* ================================= */

.visit-details-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;

  padding: 17px 20px;

  border-bottom: 1px solid #e8ebf0;
}

.visit-details-heading {
  min-width: 0;
}

.visit-details-heading h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #263445;
}

.visit-details-heading p {
  margin: 5px 0 0;
  color: #667085;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.visit-details-close {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;

  border: 1px solid #e1e5ec;
  border-radius: 6px;

  background: #ffffff;
  color: #526070;
  cursor: pointer;
}

.visit-details-close:hover {
  background: #f3f6f9;
}

/* ================================= */
/* DETAILS SCROLLABLE CONTENT        */
/* ================================= */

.visit-details-content {
  flex: 1;
  min-height: 0;

  overflow-y: auto;
  overscroll-behavior: contain;

  padding: 20px;
}

/* ================================= */
/* VISIT SUMMARY                     */
/* ================================= */

.visit-details-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 15px;

  padding: 16px;

  border: 1px solid #e8edf3;
  border-radius: 8px;

  background: #f8fafc;
}

.visit-info-field {
  display: flex;
  flex-direction: column;
  gap: 5px;

  min-width: 0;

  color: #344054;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.visit-info-label {
  color: #667085;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.visit-info-field strong {
  font-size: 13px;
  font-weight: 600;
  color: #263445;
}

.visit-info-remark {
  white-space: pre-wrap;
  line-height: 1.5;
}

.visit-info-full {
  grid-column: 1 / -1;
}

.visit-inactive-note {
  padding: 9px 12px;

  border-radius: 5px;
  background: #ffebee;

  color: #344054;
  font-size: 12px;
}

.visit-inactive-note .fa {
  margin-right: 5px;
}

/* ================================= */
/* DETAILS SECTION HEADERS           */
/* ================================= */

.visit-detail-section {
  margin-top: 22px;
}

.visit-detail-section-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  margin-bottom: 12px;
  padding-bottom: 9px;

  border-bottom: 1px solid #e8ebf0;
}

.visit-detail-section-heading h4 {
  display: flex;
  align-items: center;
  gap: 9px;

  margin: 0;

  color: #263445;
  font-size: 15px;
  font-weight: 700;
}

.visit-detail-section-heading h4 .fa {
  color: #2563a6;
}

.visit-section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 25px;
  height: 25px;
  padding: 0 7px;

  border-radius: 13px;

  background: #edf1f5;
  color: #526070;

  font-size: 12px;
  font-weight: 600;
}

/* ================================= */
/* CHECKLIST GRID                    */
/* ================================= */

.visit-checklist-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.visit-checklist-entry {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;

  min-width: 0;
  padding: 12px;

  border: 1px solid #e8edf3;
  border-radius: 7px;

  background: #ffffff;
}

.visit-checklist-name {
  flex: 1 1 120px;

  color: #344054;
  font-size: 13px;
  font-weight: 500;

  overflow-wrap: anywhere;
}

.visit-checklist-value {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  padding: 4px 9px;

  border-radius: 5px;

  background: #f2f4f7;
  color: #667085;

  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.visit-checklist-value.is-positive {
  background: #e9f7ee;
  color: #218a45;
}

.visit-checklist-value.is-negative {
  background: #fdebec;
  color: #c34a53;
}

.visit-checklist-description {
  flex: 1 1 100%;
  margin: 0;

  color: #667085;
  font-size: 12px;
  line-height: 1.5;

  overflow-wrap: anywhere;
}

/* ================================= */
/* VISIT IMAGES GRID                 */
/* ================================= */

.visit-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
}

.visit-image-thumb {
  position: relative;
  display: block;

  width: 100%;
  height: 125px;
  padding: 0;

  border: 1px solid #e1e5ec;
  border-radius: 7px;

  background: #f5f6f8;
  overflow: hidden;

  cursor: pointer;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.visit-image-thumb:hover {
  border-color: #8fb5e9;

  box-shadow:
    0 3px 12px rgba(37, 99, 166, 0.12);
}

.visit-image-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visit-image-number {
  position: absolute;
  right: 6px;
  bottom: 6px;

  padding: 3px 7px;

  border-radius: 4px;

  background: rgba(15, 23, 42, 0.75);
  color: #ffffff;

  font-size: 11px;
  font-weight: 600;
}

.visit-image-retry {
  margin-left: 10px;
  padding: 5px 10px;

  border: 1px solid #c7d8f0;
  border-radius: 5px;

  background: #eef5ff;
  color: #2563a6;

  font-size: 12px;
  cursor: pointer;
}

/* ================================= */
/* ITEM TABLE                        */
/* ================================= */

.visit-items-table-wrap {
  width: 100%;
  overflow-x: auto;

  border: 1px solid #e8edf3;
  border-radius: 7px;
}

.visit-items-table {
  width: 100%;
  border-collapse: collapse;

  font-size: 12px;
  color: #344054;
}

.visit-items-table thead th {
  padding: 12px;

  background: #f5f7fa;
  color: #475467;

  font-weight: 600;
  text-align: center;
  white-space: nowrap;

  border-bottom: 1px solid #e8edf3;
}

.visit-items-table thead th:first-child {
  text-align: left;
}

.visit-items-table tbody td {
  padding: 12px;

  border-bottom: 1px solid #edf0f4;
  text-align: center;

  font-variant-numeric: tabular-nums;
}

.visit-items-table tbody td:first-child {
  text-align: left;
  font-weight: 500;
}

.visit-items-table tbody tr:nth-child(even) {
  background: #fafbfc;
}

.visit-items-table tbody tr:last-child td {
  border-bottom: none;
}

/* ================================= */
/* EMPTY DETAIL SECTION              */
/* ================================= */

.visit-detail-empty {
  padding: 24px 12px;

  border: 1px dashed #e1e5ec;
  border-radius: 7px;

  background: #fafbfc;
  color: #667085;

  text-align: center;
  font-size: 13px;
}

/* ================================= */
/* DETAILS POPUP FOOTER              */
/* ================================= */

.visit-details-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  padding: 12px 20px;

  border-top: 1px solid #e8ebf0;
  background: #ffffff;
}

.visit-details-done {
  min-width: 88px;
  height: 36px;
  padding: 0 17px;

  border: none;
  border-radius: 5px;

  background: #007bff;
  color: #ffffff;

  font-size: 13px;
  font-weight: 600;

  cursor: pointer;
}

.visit-details-done:hover {
  background: #0056b3;
}

/* ================================= */
/* GALLERY BACKDROP                  */
/* ================================= */

.gallery-backdrop {
  position: fixed;
  inset: 0;

  z-index: 2200;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 14px;

  background: rgba(15, 23, 42, 0.73);
}

/* ================================= */
/* GALLERY DIALOG                    */
/* ================================= */

.gallery-dialog {
  width: min(760px, 100%);
  max-height: calc(100dvh - 28px);

  display: flex;
  flex-direction: column;

  min-height: 0;
  overflow: hidden;

  border-radius: 11px;
  background: #fff;

  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.24);
}

/* ================================= */
/* GALLERY HEADER                    */
/* ================================= */

.gallery-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  padding: 11px 15px;
  border-bottom: 1px solid #eaecf0;
}

.gallery-heading {
  min-width: 0;
}

.gallery-heading h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #263445;
}

.gallery-heading p {
  margin: 3px 0 0;

  color: #667085;
  font-size: 12px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gallery-close {
  flex: 0 0 29px;

  width: 29px;
  height: 29px;

  border: 1px solid #e1e5ec;
  border-radius: 6px;

  background: #fff;
  color: #526070;

  cursor: pointer;
}

.gallery-close:hover {
  background: #f3f6f9;
}

/* ================================= */
/* MAIN IMAGE AREA                   */
/* ================================= */

.gallery-stage {
  position: relative;

  height: min(55vh, 480px);
  min-height: 170px;

  display: flex;
  justify-content: center;
  align-items: center;

  background: #f5f6f8;
  overflow: hidden;
}

.gallery-stage>img {
  max-width: 100%;
  max-height: 100%;

  width: auto;
  height: auto;

  object-fit: contain;
}

/* ================================= */
/* GALLERY ARROWS                    */
/* ================================= */

.stage-arrow {
  position: absolute;
  top: 50%;

  transform: translateY(-50%);

  width: 33px;
  height: 40px;

  border: 1px solid #e3e7ed;
  border-radius: 7px;

  background: rgba(255, 255, 255, 0.95);
  color: #344054;

  cursor: pointer;
  z-index: 2;
}

.stage-arrow:hover {
  background: #fff;
  color: #007bff;
}

.stage-arrow.previous {
  left: 10px;
}

.stage-arrow.next {
  right: 10px;
}

/* ================================= */
/* GALLERY FOOTER                    */
/* ================================= */

.gallery-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;
  padding: 9px 14px;
}

.gallery-position {
  flex: 0 0 auto;

  color: #667085;
  font-size: 12px;
  font-weight: 600;
}

/* ================================= */
/* GALLERY THUMBNAILS                */
/* ================================= */

.gallery-thumbnails {
  display: flex;
  justify-content: flex-end;
  gap: 7px;

  max-width: 75%;
  overflow-x: auto;

  padding: 2px;
}

.gallery-thumb {
  flex: 0 0 auto;

  width: 52px;
  height: 43px;

  padding: 2px;

  border: 2px solid transparent;
  border-radius: 6px;

  background: #fff;
  cursor: pointer;
}

.gallery-thumb.active {
  border-color: #007bff;
}

.gallery-thumb img {
  width: 100%;
  height: 100%;

  object-fit: cover;
  border-radius: 3px;
}

/* ================================= */
/* GALLERY STATES                    */
/* ================================= */

.gallery-feedback {
  padding: 35px 15px;

  text-align: center;

  color: #667085;
  font-size: 13px;
}

.gallery-feedback .fa {
  margin-right: 7px;
}

.error-text {
  color: #bc3333;
}

/* ================================= */
/* EXISTING FILTER RESPONSIVENESS    */
/* ================================= */

@media (max-width: 1000px) {
  .dropdown-container {
    grid-template-columns:
      repeat(3, minmax(0, 1fr));

    column-gap: 12px;
    row-gap: 12px;
  }

  .dealer-report-page .search-container {
    grid-column: span 3;
  }
}

/* TABLETS */

@media (max-width: 800px) {
  .dropdown-container {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));

    column-gap: 10px;
    row-gap: 12px;
  }

  .dealer-report-page .search-container {
    grid-column: span 2;
    gap: 10px;
  }

  .gallery-stage {
    height: 48vh;
  }
}

/* ================================= */
/* DETAILS POPUP RESPONSIVENESS      */
/* ================================= */

@media (max-width: 650px) {
  .visit-details-backdrop {
    padding: 10px;
  }

  .visit-details-dialog {
    max-height: calc(100dvh - 20px);
  }

  .visit-details-header {
    padding: 13px 14px;
  }

  .visit-details-heading h3 {
    font-size: 16px;
  }

  .visit-details-content {
    padding: 14px;
  }

  .visit-details-summary {
    gap: 12px;
    padding: 12px;
  }

  .visit-checklist-grid {
    grid-template-columns: 1fr;
  }

  .visit-images-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .visit-items-table {
    min-width: 440px;
  }

  .visit-details-footer {
    padding: 10px 14px;
  }
}

/* ================================= */
/* MOBILE                            */
/* ================================= */

@media (max-width: 480px) {
  .dealer-report-page {
    padding: 8px;
  }

  .dropdown-container {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 10px;
  }

  .dropdown-item {
    width: 100%;
    max-width: none;
  }

  .dealer-report-page .search-container {
    grid-column: span 1;

    display: grid;
    grid-template-columns:
      repeat(2, minmax(0, 1fr));

    width: 100%;
    gap: 10px;
  }

  .dealer-report-page .search-container .btn {
    width: 100%;
    min-width: 0;
    padding: 0 10px;
  }

  .report-toolbar {
    padding: 10px;
  }

  .gallery-stage {
    height: 43vh;
  }

  .gallery-bottom {
    padding: 7px 10px;
  }
}

@media (max-width: 380px) {
  .visit-details-summary {
    grid-template-columns: 1fr;
  }

  .visit-image-thumb {
    height: 100px;
  }
}

/* ================================= */
/* INACTIVE DEALER DROPDOWN OPTIONS  */
/* ================================= */

/* Inactive: red background, black text */

.dropdown-item select option.inactive-dealer-option {
  background-color: #ffebee !important;
  color: #000000 !important;
}

/* Active: white background, black text */

.dropdown-item select option:not(.inactive-dealer-option) {
  background-color: #ffffff;
  color: #000000;
}
</style>